// SmartReportUploadViewer.jsx
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Share, MessageCircle } from 'lucide-react';
// import "../../index.css";
import Anatomy from "../../Assets/Images/bodyAnatomyReport.svg"
import CureBayLogo from "../../Assets/Images/curebay_logo.svg";
import shareIcon from "../../Assets/Images/share.svg";
import downloadIcon from "../../Assets/Images/downloadReport.svg";

import { toast } from "react-toastify";

function SmartReportUploadViewer({ reportData: propData, patient }) {
  // Fallback to localStorage if patient prop not provided
  if (!patient) {
    patient = JSON.parse(localStorage.getItem("activePatient"));
  }

  // Retrieve reportData from props or location state
  const location = useLocation();
  const state = location.state || {};
  const reportData = propData || state.reportData;
  
  // ADD:
const userData = JSON.parse(localStorage.getItem("loginObj")) || {};

  // Share modal state
  const [visible, setVisible] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [helperText, setHelperText] = useState(
    "Enter your phone number to share the report."
  );

  // Responsive/mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLoading, setIsLoading] = useState(false);

  const pdfRef = useRef(null);

  const validatePhone = (value) => /^[0-9]{10}$/.test(value);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Helpers for value positioning
  function parseRange(rangeString) {
    if (!rangeString) return null;
    if (rangeString.toLowerCase().includes("unknown")) return null;
    const parts = rangeString.split("-").map((p) => parseFloat(p.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { minVal: parts[0], maxVal: parts[1] };
    }
    return null;
  }

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    // allow only digits
    if (!/^\d*$/.test(val)) return;
    setPhoneNumber(val);

    if (val.length === 0) {
      setPhoneError(false);
      setHelperText("Enter your phone number to share the report.");
    } else if (!validatePhone(val)) {
      setPhoneError(true);
      setHelperText("Phone number must be exactly 10 digits");
    } else {
      setPhoneError(false);
      setHelperText("");
    }
  };

  function clampDotPosition(pct) {
    if (pct < 10) return 10;
    if (pct > 90) return 90;
    return pct;
  }

  function getFixedBarPositions(minVal, maxVal, userValue) {
    const range = maxVal - minVal;
    let margin = range * 0.2;
    let scaleMin = minVal - margin;
    let scaleMax = maxVal + margin;
    if (userValue < scaleMin) scaleMin = userValue;
    if (userValue > scaleMax) scaleMax = userValue;
    const greenStartPct = ((minVal - scaleMin) / (scaleMax - scaleMin)) * 100;
    const greenEndPct = ((maxVal - scaleMin) / (scaleMax - scaleMin)) * 100;
    const userValPctRaw = ((userValue - scaleMin) / (scaleMax - scaleMin)) * 100;
    const userValPct = clampDotPosition(userValPctRaw);
    const inRange = userValue >= minVal && userValue <= maxVal;
    return { greenStartPct, greenEndPct, userValPct, inRange };
  }

  // WhatsApp share API call
  const smartReport = async () => {
    setIsLoading(true);
    try {
      const url = new URL(
        `${process.env.REACT_APP_SMART_REPORT_BASEURL}get_smart_report_whats_app/`
      );
      url.searchParams.append("OrderID", reportData?.orderid);
      url.searchParams.append("mobile", phoneNumber);

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { accept: "application/json" },
      });

      if (!res.ok) throw new Error(res.status);
      const data = await res.json();
      console.log("Smart report response:", data);
      setVisible(false);
      toast.success('Smart Report Shared Successfully to your whatsapp');
    } catch (err) {
      console.error("smartReport error:", err);
      toast.error('Something went wrong');
      setVisible(false);
    } finally {
      setIsLoading(false);
      setPhoneNumber('');
      setVisible(false);
    }
  };

  // Download report handler
  const handleDownload = () => {
    const url = reportData?.smart_report_url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // Dialog header
  const dialogHeader = (
    <div className="flex items-center gap-2">
      <Share size={24} />
      <span>Share Your Report</span>
    </div>
  );

  // Dialog footer
  const dialogFooter = (
    <div className="flex justify-end gap-2">
      <Button 
        label="Cancel" 
        severity="secondary" 
        onClick={() => setVisible(false)}
        outlined
      />
      <Button
        label="Share Report"
        icon={<MessageCircle size={16} />}
        onClick={smartReport}
        disabled={!validatePhone(phoneNumber) || isLoading}
        loading={isLoading}
        className="bg-green-500 border-green-500"
      />
    </div>
  );

  // Intro page JSX
  const renderIntroPage = () => (
    <div className="pdf-page page-break">
      <div className="relative min-h-screen bg-[#004273] text-white font-sans">
        <div
          className="absolute inset-0 z-0 gradient-bg"
          style={{
            background: `
                radial-gradient(
                  circle at 15% 25%,
                  rgba(0, 179, 220, 0.8) 0%,
                  rgba(0, 179, 220, 0) 60%
                )
              `,
          }}
        />
        <header className="relative flex items-center px-6 py-4 md:px-8">
          <img
            src={CureBayLogo}
            alt="CureBay Logo"
            className="h-10 w-auto md:h-12 mr-2"
          />
        </header>
        <div className="relative px-6 mt-4 z-10 md:px-8">
          <h1 className="text-2xl font-light text-white leading-tight italic">
            Smart Digital
          </h1>
          <h2 className="text-3xl font-semibold mt-1 text-[#42BA85] md:text-4xl">
            Health Report
          </h2>
        </div>
        <div className="relative z-10 flex justify-center mt-6 px-6 md:justify-end md:px-8">
          <img
            src={Anatomy}
            alt="Body Silhouette"
            className="w-48 md:w-64 lg:w-72"
          />
        </div>
        <div className="relative px-6 mt-4 z-10 md:absolute md:bottom-44 md:left-8 md:h-20 md:px-0">
          <p className="text-2xl text-white font-bold md:text-3xl">
            {reportData?.patient_info?.name}
          </p>
          <p className="text-xl mt-1 italic text-white">
            {reportData?.patient_info?.age}, {reportData?.patient_info?.gender}
          </p>
        </div>
        <footer className="absolute bottom-0 w-full bg-[#42BA85] text-white text-center py-3 md:py-4 z-10">
          <p className="text-sm">
            For any questions regarding your report, feel free to call us at
            +918335000999
          </p>
        </footer>
      </div>
    </div>
  );

  // Summary page JSX
  const renderSummaryPage = () => (
    <div className="pdf-page page-break">
      <div className="bg-white text-black font-sans relative">
        <header className="bg-[#004171] text-white px-6 py-4 flex items-center justify-between">
          <img src={CureBayLogo} alt="CureBay Logo" className="h-8 w-auto" />
          <h1 className="text-lg font-semibold">Smart Laboratory Report</h1>
        </header>
        <div className="mx-6 mt-4 flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex space-x-3">
            <button 
              className="share-btn flex items-center px-4 py-2 rounded bg-[#42BA85] text-white hover:bg-brand-primary-dark transition-colors duration-200" 
              onClick={() => setVisible(true)}
            >
              <img src={shareIcon} alt="Share" className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Share</span>
            </button>
            <button
              onClick={handleDownload}
              className="download-btn flex items-center px-4 py-2 rounded bg-white text-[#42BA85] hover:bg-brand-primary-dark transition-colors duration-200"
            >
              <img src={downloadIcon} alt="Download" className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Download</span>
            </button>
          </div>
        </div>
        <div className="p-5 smart-report-bg rounded-md mt-3">
          <div className="flex flex-col md:flex-row">
            <div className="md:flex-1">
              <div className="space-y-1">
                <p>
                  <strong className="font-inter font-semibold text-sm leading-4 tracking-normal  mr-2">
                    Patient Name:
                  </strong>
                  <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                    {reportData?.patient_info?.name}
                  </span>
                </p>
                <p>
                  <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                    Age/Gender:
                  </strong>
                  <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                    {reportData?.patient_info?.age},{" "}
                    {reportData?.patient_info?.gender}
                  </span>
                </p>
                <p>
                  <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                    Sample Type:
                  </strong>
                  <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                    {reportData?.subtype}
                  </span>
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center">
              <div className="w-px bg-gray-300 h-full mx-4"></div>
            </div>
            <div className="md:flex-1 mt-4 md:mt-0">
              <div className="space-y-1">
                <p>
                  <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                    Patient Id:
                  </strong>
                  <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                    {userData?.code}
                  </span>
                </p>
                <p>
                  <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                    Test Conducted:
                  </strong>
                  <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                    {reportData?.specimen_info?.collection_date_time}
                  </span>
                </p>
                <p>
                  <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                    Sample Collected:
                  </strong>
                  <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                    {reportData?.specimen_info?.received_date_time}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full mt-3 mb-3">
          <img
            crossOrigin="anonymous"
            className="w-full h-auto"
            src={reportData?.smart_report_banner}
            alt="Banner Placeholder"
          />
        </div>
        <div className="px-6 mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-center ">
          <div>
            <h2 className="text-2xl font-bold text-brand-primary mb-2">
              Health Summary
            </h2>
            <p className="text-sm text-gray-tertiary mb-4">
              Here is a highlighted summary of your health report
            </p>
            <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-4xl">
              {Array.isArray(reportData?.summary) ? (
                <ul className="list-disc list-inside space-y-2">
                  {reportData.summary.map((item, index) => {
                    const text = item.replace(/^\s*\d+\.\s*/, "");
                    return (
                      <li key={index} className="text-sm text-gray-600">
                        {text}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-600">
                  {reportData?.summary}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-center self-center">
            <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-xs">
              <h3 className="text-lg font-semibold text-center text-[#004171]">
                Your Health Score
              </h3>
              <hr className="border-t border-gray-200 my-2" />
              <div className="relative w-40 h-40 mx-auto mt-4">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#E5E5E5"
                    strokeWidth="10"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="#92E3A9"
                    strokeWidth="10"
                    strokeDasharray={`${(reportData?.health_score || 0) * 2.83} 283`}
                    transform="rotate(-90 50 50)"
                  />
                </svg>
                <div
                  className="absolute top-1/2 left-1/2 w-4 h-4 bg-[#92E3A9] rounded-full"
                  style={{
                    transform: `
              rotate(${(reportData?.health_score || 0) * 3.6}deg)
              translate(-50%, -50%)
              translate(0, -45px)
            `,
                    transformOrigin: "50% 50%",
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-28 h-28  rounded-full flex items-center justify-center"
                    style={{ background: "#004172" }}
                  >
                    <span className="text-2xl font-bold text-white">
                      {reportData?.health_score || 0}%
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-textGray text-center mt-3">
                Out of 100
              </p>
            </div>
          </div>
        </div>
        {reportData?.recommendations &&
          (Array.isArray(reportData.recommendations) ? (
            reportData.recommendations.length > 0 && (
              <div className="p-5 rounded-md mt-8 smart-report-bg">
                <h2 className="text-xl font-bold text-[#004171] mb-5">
                  Recommendation
                </h2>
                <ul className="list-disc list-inside space-y-2">
                  {reportData.recommendations.map((rec, index) => {
                    const cleanedItem = rec.replace(/^\d+(\.|:|\))?\s*/, "");
                    return (
                      <li key={index} className="text-sm text-gray-600">
                        {cleanedItem}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )
          ) : (
            <div className="p-5 rounded-md mt-8 smart-report-bg">
              <h2 className="text-xl font-bold text-brand-primary mb-5">
                Recommendation
              </h2>
              <p className="text-sm text-gray-600">
                {reportData.recommendations}
              </p>
            </div>
          ))}
        <footer className="bg-[#42BA85] text-white text-center py-4 mt-8">
          <p className="text-sm">
            For any questions regarding your report, feel free to call us at
            +918335000999
          </p>
        </footer>
      </div>
    </div>
  );

  // Test pages JSX (full list)
  const renderTestPage = (testList) => (
    <div className="bg-white text-black font-sans relative">
      <header className="bg-[#004171] text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <img src={CureBayLogo} alt="CureBay Logo" className="h-8 w-auto" />
        </div>
        <h1 className="text-lg font-semibold">Smart Laboratory Report</h1>
      </header>
      <div className="mx-6 mt-4 flex flex-col md:flex-row md:items-center md:justify-between">
        <div className="flex space-x-3">
          <button 
            className="share-btn flex items-center px-4 py-2 rounded bg-[#42BA85] text-white hover:bg-brand-primary-dark transition-colors duration-200" 
            onClick={() => setVisible(true)}
          >
            <img src={shareIcon} alt="Share" className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">Share</span>
          </button>
          <button
            onClick={handleDownload}
            className="download-btn flex items-center px-4 py-2 rounded bg-white text-[#42BA85] hover:bg-brand-primary-dark transition-colors duration-200"
          >
            <img src={downloadIcon} alt="Download" className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">Download</span>
          </button>
        </div>
      </div>
      <div className="p-5 smart-report-bg rounded-md mt-3">
        <div className="flex flex-col md:flex-row">
          <div className="md:flex-1">
            <div className="space-y-1">
              <p>
                <strong className="font-inter font-semibold text-sm leading-4 tracking-normal  mr-2">
                  Patient Name:
                </strong>
                <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                  {reportData?.patient_info?.name}
                </span>
              </p>
              <p>
                <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                  Age/Gender:
                </strong>
                <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                  {reportData?.patient_info?.age},{" "}
                  {reportData?.patient_info?.gender}
                </span>
              </p>
              <p>
                <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                  Sample Type:
                </strong>
                <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                  {reportData?.subtype}
                </span>
              </p>
            </div>
          </div>
          <div className="hidden md:flex items-center">
            <div className="w-px bg-gray-300 h-full mx-4"></div>
          </div>
          <div className="md:flex-1 mt-4 md:mt-0">
            <div className="space-y-1">
              <p>
                <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                  Patient Id:
                </strong>
                <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                  {userData?.code}
                </span>
              </p>
              <p>
                <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                  Test Conducted:
                </strong>
                <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                  {reportData?.specimen_info?.collection_date_time}
                </span>
              </p>
              <p>
                <strong className="font-inter font-semibold text-sm leading-4 tracking-normal text-brand-primary mr-2">
                  Sample Collected:
                </strong>
                <span className="font-inter font-semibold text-sm leading-4 tracking-normal">
                  {reportData?.specimen_info?.received_date_time}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="mx-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#004171]">
            {reportData?.title || "XYZ Test Name"}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testList.map((finding, index) => {
            const rangeObj = parseRange(finding.reference_range);
            if (!rangeObj) {
              return (
                <div
                  key={index}
                  className="border border-gray rounded p-4 shadow-sm smart-report-bg"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-semibold text-[#004171]">
                      {finding.name || "Test Parameter"}
                    </h3>
                    <span className="px-2 py-1 rounded text-sm">
                      {finding.flag || ""}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-6">
                    <strong>Your Value:</strong> {finding.value} {finding.unit || ""}
                  </p>
                  <div className="bg-white p-3 rounded mt-4">
                    <p className="text-sm text-textGray">
                      <strong>About test: </strong>
                      {finding?.about_test}
                    </p>
                  </div>
                </div>
              );
            }
            const userValue = parseFloat(finding.value) || 0;
            const { greenStartPct, greenEndPct, userValPct, inRange } =
              getFixedBarPositions(rangeObj.minVal, rangeObj.maxVal, userValue);
            const userLabelColor = inRange ? "text-[#42BA85]" : "text-danger";
            const dotColor = inRange ? "bg-[#42BA85]" : "bg-danger";
            let statusLabel = inRange ? "Good" : "Critical";
            let badgeClasses = inRange
              ? "bg-[#42BA85] text-white px-2 py-1 rounded text-sm"
              : "bg-danger text-white px-2 py-1 rounded text-sm";
            if (finding.flag?.toLowerCase() === "high") {
              statusLabel = "High";
              badgeClasses = "bg-danger text-danger px-2 py-1 rounded text-sm";
            } else if (finding.flag?.toLowerCase() === "low") {
              statusLabel = "Low";
              badgeClasses = "bg-yellow-100 text-yellow-600 px-2 py-1 rounded text-sm";
            }
            return (
              <div
                key={index}
                className="border border-gray rounded p-4 shadow-sm smart-report-bg"
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-base font-semibold text-[#004171]">
                    {finding.name || "Test Parameter"}
                  </h3>
                  <span className={badgeClasses}>{statusLabel}</span>
                </div>
                <p className="text-sm text-textGray">
                  Normal Range:
                  <strong>{finding.reference_range} {finding.unit}</strong>
                </p>
                <div className="relative w-full h-2 bg-textGray rounded mt-16">
                  <div
                    className="absolute top-0 left-0 h-2 bg-danger"
                    style={{ width: `${greenStartPct}%` }}
                  />
                  <div
                    className="absolute top-0 h-2 bg-[#42BA85]"
                    style={{ left: `${greenStartPct}%`, width: `${greenEndPct - greenStartPct}%` }}
                  />
                  <div
                    className="absolute top-0 right-0 h-2 bg-danger"
                    style={{ width: `${100 - greenEndPct}%` }}
                  />
                  <div
                    className="absolute flex flex-col items-center"
                    style={{ left: `${userValPct}%`, transform: "translateX(-50%)", bottom: "100%" }}
                  >
                    <span className={`text-sm font-medium whitespace-nowrap mb-1 ${userLabelColor}`}>
                      {userValue} {finding.unit}
                    </span>
                    <div className={`w-3 h-3 rounded-full mb-1 ${dotColor}`} />
                    <div className={`${dotColor}`} style={{ width: "1px", height: "8px" }} />
                  </div>
                </div>
                <div className="bg-white p-3 rounded mt-4">
                  <p className="text-sm text-textGray">
                    <strong>About test: </strong>
                    {finding?.about_test}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <footer className="bg-[#42BA85] text-white text-center py-4 mt-8">
        <p className="text-sm">
          For any questions regarding your report, feel free to call us at
          +918335000999
        </p>
      </footer>
    </div>
  );

  return (
    <>
      <div id="report-content" className={`${isMobile ? '' : 'popup-container'} bg-brand-primary text-white font-sans`}>
        {renderIntroPage()}
        {renderSummaryPage()}
        {reportData?.key_findings?.length > 0 && renderTestPage(reportData.key_findings)}
      </div>
      <div id="report-pdf" ref={pdfRef} className="w-full h-auto" style={{ position: 'fixed', top: 0, left: 0, opacity: 0, pointerEvents: 'none' }}>
        {renderIntroPage()}
        {renderSummaryPage()}
        {reportData?.key_findings?.length > 0 && renderTestPage(reportData.key_findings)}
      </div>
      
      <Dialog 
        header={dialogHeader}
        visible={visible} 
        style={{ width: '400px' }} 
        onHide={() => setVisible(false)}
        footer={dialogFooter}
        modal
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Please enter your phone number below to receive the report.
          </p>
          
          <div className="space-y-2">
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <InputText
              id="phoneNumber"
              value={phoneNumber}
              onChange={handlePhoneChange}
              placeholder="Enter 10-digit phone number"
              className={`w-full ${phoneError ? 'p-invalid' : ''}`}
              maxLength={10}
            />
            {helperText && (
              <Message 
                severity={phoneError ? "error" : "info"} 
                text={helperText} 
                className="mt-1"
              />
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default SmartReportUploadViewer;